import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useFunnelsApiFacade } from '../../../../apis/facades/useFunnelsApiFacade';
import {
  addSfFunnel,
  selectIsFunnelMapEmpty,
} from '../../../../reducers/united-canvas/unitedCanvasReducer';
import { setTransientFunnel } from '../../../../reducers/canvasPageStateReducer';
import { ACTIONS_EVENTS } from '../../../../services/events';
import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { FunnelType } from '../../../../types/enums/FunnelType';
import { setFunnelStatusFromFunnel } from '../../../../reducers/funnelStatusReducer';

export const useSfAddFunnelToTheCanvas = (crmOrgId: string) => {
  const dispatch = useDispatch();
  const sendBiEvent = useSendBiEvent();
  const { get_funnel, post_funnel } = useFunnelsApiFacade();
  const isFunnelMapEmpty = useSelector(selectIsFunnelMapEmpty);

  const onCreateSfFunnel = useCallback(
    async ({
      funnelName,
      funnelDetails,
      position,
      sectionId,
    }: {
      funnelName: string;
      funnelDetails: FunnelDetails;
      position: { column: number; row: number };
      sectionId?: string;
    }) => {
      const funnel = await post_funnel(funnelName, crmOrgId, funnelDetails);
      sendBiEvent({
        name: ACTIONS_EVENTS.funnelsAddFunnel,
        props: { object: funnel.funnelDetails.leadingObject.objectName },
      });
      dispatch(
        addSfFunnel({
          funnel,
          position,
          sectionId,
        }),
      );

      return funnel;
    },
    [crmOrgId, dispatch, post_funnel, sendBiEvent],
  );

  const addSfFunnelToTheCanvas = useCallback(
    async ({
      funnelId,
      isImportedRecordType,
    }: {
      funnelId: string;
      isImportedRecordType: boolean;
    }) => {
      const funnel = await get_funnel(funnelId);
      dispatch(setFunnelStatusFromFunnel(funnel));

      if (isFunnelMapEmpty) {
        //by product definition first funnel should be dropped automatically on canvas
        dispatch(
          addSfFunnel({
            funnel,
            position: {
              column: 0,
              row: 0,
            },
          }),
        );
        return;
      }

      dispatch(
        setTransientFunnel({
          type: FunnelType.SALESFORCE,
          importType: 'import',
          data: { funnel },
          name: funnel.name,
          position: { column: 0, row: 0 },
          isPlacingFunnel: true,
          isImportedRecordType,
        }),
      );
    },
    [dispatch, get_funnel, isFunnelMapEmpty],
  );

  return {
    onCreateSfFunnel,
    addSfFunnelToTheCanvas,
  };
};
